import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Phone, Building2, MapPin, User, PlusCircle, KeyRound, Copy, Loader2, Send } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 p-3 bg-[#050816]/30 rounded-lg">
    <Icon className="h-5 w-5 text-[#C6A14A]" />
    <div>
      <p className="text-sm text-[#E8E4DE]/60">{label}</p>
      <p className="text-white" data-testid={testId}>{value || '—'}</p>
    </div>
  </div>
);

export const ClientProfileCard = ({ user }) => {
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(user.welcome_email_sent_at);

  const sendWelcome = async () => {
    setSending(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/clients/${user.id}/welcome-email`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Email could not be sent');
      setSentAt(data.sent_at);
      toast.success(`Welcome email sent to ${data.email}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };
  const isAdminUser = user?.roles?.includes('admin');

  const generate = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/clients/${user.id}/password-link`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not generate link');
      setLink(data.link);
      await navigator.clipboard?.writeText(data.link).catch(() => {});
      toast.success('Set-password link copied. It works once and expires in about an hour.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#0f1724] border-white/10" data-testid="client-profile-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 flex-wrap">
        <CardTitle className="text-lg text-white">Client Profile</CardTitle>
        <Button asChild className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]">
          <Link to={`/admin/clients/${user.id}/new-order`} data-testid="create-order-for-client-btn">
            <PlusCircle className="h-4 w-4 mr-2" /> Create Order for Client
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <InfoRow icon={User} label="Name" value={user.full_name} testId="client-name" />
          <InfoRow icon={Phone} label="Phone" value={user.phone} testId="client-phone" />
          <InfoRow icon={Building2} label="Brokerage" value={user.company} testId="client-company" />
          <InfoRow icon={MapPin} label="Address" value={user.address} testId="client-address" />
        </div>
        {!isAdminUser && (
          <div className="pt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={sending}
              className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="send-welcome-email-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {sentAt ? 'Resend welcome email' : 'Send welcome email'}
            </Button>
            <Button size="sm" variant="outline" onClick={generate} disabled={busy}
              className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="generate-password-link-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Copy set-password link
            </Button>
            </div>
            {sentAt && (
              <p className="text-xs text-[#E8E4DE]/50" data-testid="welcome-email-sent-at">
                Welcome email last sent {new Date(sentAt).toLocaleString()}
              </p>
            )}
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent className="bg-[#0f1724] border-white/10 text-white" data-testid="welcome-email-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-light">Send welcome email?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#E8E4DE]/60">
                    {user.email} will get a branded RC Studios email with a one-time link to set their password (expires in about an hour).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-white/10 bg-transparent text-[#E8E4DE] hover:bg-white/5" data-testid="welcome-email-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={sendWelcome} className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="welcome-email-confirm-btn">Send</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {link && (
              <div className="flex gap-2">
                <Input readOnly value={link} onFocus={(e) => e.target.select()}
                  className="bg-[#050816] border-white/10 text-[#E8E4DE]/80 text-xs" data-testid="password-link-input" />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success('Copied'); }}
                  className="border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="copy-password-link-btn">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
